import { AxiosProgressEvent } from "axios";
import apiConfig from "constants/apiConfig"
import { SavedAddress } from "types";
import { MilestoneData, ProcessMilestonePayload } from "types/collaboration"
import { CollabDeletionRequestShowResponse, CollabDeletionRequestsResponse } from "types/collaboration-deletion-request";
import axiosClient from "utils/axiosClient"

export const fetchCollaborationTasks = async (payload: any = {}) => {
    const response = await axiosClient.get(apiConfig.FETCH_COLLABORATION_TASKS.url, { params: payload });
    return response.data
}

export const fetchCollaborationMilestone = async (campaignId: string, collaborationId: string, milestoneId: string) => {
    const response = await axiosClient.get(apiConfig.FETCH_COLLABORATION_MILESTONE.url
        .replace('<campaignId>', campaignId)
        .replace('<collaborationId>', collaborationId)
        .replace('<milestoneId>', milestoneId)
    );

    return response.data as MilestoneData
}

export const fetchCollaborationPermalinkFile = async (
    campaignId: string,
    collaborationId: string,
    permalink: string,
    action: string | null = null,
    onDownloadProgress?: ((progressEvent: AxiosProgressEvent) => void)
) => {
    const isDownload = action === 'download';

    const response = await axiosClient.post(
        apiConfig.FETCH_COLLABORATION_PERMALINK_FILE.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId),
        { permalink, ...(isDownload ? { action } : {}) },
        isDownload ? { responseType: 'blob', onDownloadProgress } : undefined
    );

    return isDownload ? response.data : response.data;
};

export const processCollaborationMilestoneStep = async (campaignId: string, collaborationId: string, milestoneId: string, payload: ProcessMilestonePayload) => {

    const formData = new FormData();

    for (let [key, value] of Object.entries(payload)) {
        const formValue = Array.isArray(value) ? value.join(',') : value;
        formData.append(key, formValue);
    }

    const response = await axiosClient.post(apiConfig.PROCESS_COLLABORATION_MILESTONE_STEP.url
        .replace('<campaignId>', campaignId)
        .replace('<collaborationId>', collaborationId)
        .replace('<milestoneId>', milestoneId),
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );

    return response.data;
}

export const submitStepDeadline = async (campaignId: string, collaborationId: string, stepSlug: string | number, date: string) => {
    const response = await axiosClient.post(apiConfig.SUBMIT_STEP_DEADLINE.url
        .replace('<campaignId>', campaignId)
        .replace('<collaborationId>', collaborationId)
        .replace('<stepSlug>', String(stepSlug)),
        { date }
    );

    return response.data;
}

export const fetchAddress = async (addressUUID: string) => {
    const response = await axiosClient.get(apiConfig.FETCH_ADDRESS.url.replace('<addressUUID>', addressUUID));
    return response.data.address as SavedAddress;
}

export const getSignedGCSUploadURL = async (campaignId: string, collaborationId: string, payload: { extension: string, content_type: string }): Promise<{ upload_url: string, uuid: string }> => {
    const response = await axiosClient.post(apiConfig.GET_SIGNED_GCS_URL.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId), payload);

    return response.data;
}

export const uploadFile = async (file: File, signedUrl: string) => {
    await fetch(signedUrl, {
        method: "PUT",
        body: file
    })
};

export const uploadLargeFileInChunks = async (
    file: File,
    uploadURL: string,
    onProgress?: (progress: { uploadedBytes: number; totalBytes: number; percentage: number }) => void,
    chunkSize: number = 5 * 1024 * 1024, // 5MB default
): Promise<void> => {
    const totalSize = file.size;
    let offset = 0;

    while (offset < totalSize) {
        const chunk = file.slice(offset, offset + chunkSize);
        const chunkSizeActual = chunk.size;
        const startByte = offset;
        const endByte = offset + chunkSizeActual - 1;

        const headers: HeadersInit = {
            'Content-Type': file.type || 'application/octet-stream',
            'Content-Range': `bytes ${startByte}-${endByte}/${totalSize}`,
        };

        try {
            const response = await fetch(uploadURL, {
                method: 'PUT',
                headers,
                body: chunk,
            });

            // If fetch succeeded and status is OK
            if ([200, 201, 308].includes(response.status)) {
                offset += chunkSizeActual;

                if (onProgress) {
                    onProgress({
                        uploadedBytes: offset,
                        totalBytes: totalSize,
                        percentage: Math.min(100, (offset / totalSize) * 100),
                    });
                }

                if (response.status === 200 || response.status === 201) {
                    // Treat as final chunk successfully uploaded
                    break;
                }

            } else {
                const text = await response.text();
                throw new Error(`Upload failed with status ${response.status}: ${text}`);
            }

        } catch (err: any) {
            // Detect likely CORS error case:
            const isFinalChunk = endByte + 1 === totalSize;
            const isCORSLike = err instanceof TypeError && (err.message.includes("Failed to fetch") || err.message.includes("Load failed"));

            if (isFinalChunk && isCORSLike) {
                console.warn('CORS blocked final chunk response, but assuming upload was successful.');
                // Treat as success and continue
                if (onProgress) {
                    onProgress({
                        uploadedBytes: totalSize,
                        totalBytes: totalSize,
                        percentage: 100,
                    });
                }
                break;
            }

            console.error('Upload failed at chunk', { startByte, endByte, error: err });
            throw err;
        }
    }
};

export const linkGCSFileWithMilestoneStep = async (campaignId: string, collaborationId: string, payload: { uuid: string, extension: string, content_type: string, step: string }) => {
    const response = await axiosClient.post(apiConfig.LINK_UPLOADED_GCS_FILE_WITH_MILESTONE_STEP.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId), payload);

    return response.data;
}

export const processCollaborationMilestoneMedia = async (campaignId: string, collaborationId: string, milestoneId: string, payload: ProcessMilestonePayload) => {

    const formData = new FormData();

    for (let [key, value] of Object.entries(payload)) {
        const formValue = Array.isArray(value) ? value.join(',') : value;
        formData.append(key, formValue);
    }

    const response = await axiosClient.post(apiConfig.PROCESS_COLLABORATION_MILESTONE_STEP.url
        .replace('<campaignId>', campaignId)
        .replace('<collaborationId>', collaborationId)
        .replace('<milestoneId>', milestoneId),
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );

    return response.data;
}

export const fetchAgencies = async (q: string = ''): Promise<any[]> => {
    const response = await axiosClient.get(apiConfig.FETCH_AGENCIES.url, { params: { q } })
    return response.data;
}

export const fetchCampaigns = async (q: string = '', agency_id: string | null = null): Promise<any[]> => {
    const response = await axiosClient.get(apiConfig.SEARCH_CAMPAIGNS.url, { params: { q, ...(agency_id ? { agency_id } : {}) } })
    return response.data;
}

export const fetchHubs = async (q: string = ''): Promise<any[]> => {
    const response = await axiosClient.get(apiConfig.FETCH_HUBS.url, { params: { q } })
    return response.data;
}


export const undoCollaborationStep = async (campaignId: string, collaborationId: string, step: string) => {
    const response = await axiosClient.post(apiConfig.UNDO_COLLABORATION_STEP.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId), { step });
    return response.data;
}

export const resetMilestone = async (campaignId: string, collaborationId: string, id: string) => {
    const response = await axiosClient.post(apiConfig.RESET_COLLAB.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId), { id });
    return response.data;
}

export const resetCollab = async (campaignId: string, collaborationId: string) => {
    const response = await axiosClient.post(apiConfig.RESET_COLLAB.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId));
    return response.data;
}

export const forceCompleteMilestone = async (campaignId: string, collaborationId: string) => {
    const response = await axiosClient.post(apiConfig.FORCE_COMPLETE_MILESTONE.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId));
    return response.data;
}


export const refreshSteps = async (campaignId: string, collaborationId: string, milestoneId: string) => {
    const response = await axiosClient.post(apiConfig.REFRESH_STEPS.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId).replace('<milestoneId>', milestoneId));
    return response.data;
}

export const updateCTA = async (campaignId: string, collaborationId: string, milestoneId: string, data: { url: string, [key: string]: any }) => {
    const response = await axiosClient.post(apiConfig.UPDATE_CTA_URL.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId).replace('<milestoneId>', milestoneId), data);
    return response.data;
}

export const forceProcessStep = async (campaignId: string, collaborationId: string, step: string) => {
    const response = await axiosClient.post(apiConfig.FORCE_PROCESS_STEP.url.replace('<campaignId>', campaignId).replace('<collaborationId>', collaborationId), { step });
    return response.data;
}

export const attachIGPermalink = async (campaignId: string, collaborationId: string, permalink: string) => {
    const response = await axiosClient.post(
        apiConfig.ATTACH_IG_PERMALINK.url
            .replace('<campaignId>', campaignId)
            .replace('<collaborationId>', collaborationId),
        { permalink }
    );
    return response.data;
}

export const fetchURLMetadata = async (url: string) => {
    const response = await axiosClient.post(apiConfig.FETCH_URL_METADATA.url, { url });
    return response.data;
}


export const storeCollabRemovalRequest = async (
    campaignId: string,
    collaborationId: string,
    payload: { reason: string; file?: File }
) => {
    const formData = new FormData();
    formData.append('reason', payload.reason);
    if (payload.file) {
        formData.append('file', payload.file);
    }

    const response = await axiosClient.post(
        apiConfig.STORE_COLLAB_REMOVAL_REQUEST.url
            .replace('<campaignId>', campaignId)
            .replace('<collaborationId>', collaborationId),
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );

    return response.data;
}



export const fetchCollaborationDeletionRequests = async (
    filters: Record<string, unknown> = {}
): Promise<CollabDeletionRequestsResponse> => {
    const response = await axiosClient.get(apiConfig.FETCH_COLLAB_DELETION_REQUESTS.url, { params: filters });
    return response.data as CollabDeletionRequestsResponse;
}

export const showCollaborationDeletionRequest = async (
    campaignId: string,
    collaborationId: string,
    requestId: string | number
): Promise<CollabDeletionRequestShowResponse> => {
    const response = await axiosClient.get(apiConfig.SHOW_COLLAB_DELETION_REQUEST.url
        .replace('<campaignId>', campaignId)
        .replace('<collaborationId>', collaborationId)
        .replace('<requestId>', String(requestId))
    );
    return response.data as CollabDeletionRequestShowResponse;
}

const assertDeletionActionSuccess = (data: { status?: boolean; message?: string; error?: string }, fallback: string) => {
    if (data?.status === false) {
        throw new Error(typeof data.error === 'string' ? data.error : (data.message || fallback));
    }
    return data;
}

export const acceptCollaborationDeletionRequest = async (
    campaignId: string,
    collaborationId: string
) => {
    const response = await axiosClient.delete(
        apiConfig.ACCEPT_COLLAB_DELETION_REQUEST.url
            .replace('<campaignId>', campaignId)
            .replace('<collaborationId>', collaborationId)
    );
    return assertDeletionActionSuccess(response.data, 'Failed to accept deletion request.');
}

export const rejectCollaborationDeletionRequest = async (
    campaignId: string,
    collaborationId: string,
    requestId: string | number,
    remark: string
) => {
    const response = await axiosClient.post(
        apiConfig.DECLINE_COLLAB_DELETION_REQUEST.url
            .replace('<campaignId>', campaignId)
            .replace('<collaborationId>', collaborationId)
            .replace('<requestId>', String(requestId)),
        { remark }
    );
    return assertDeletionActionSuccess(response.data, 'Failed to reject deletion request.');
}